<!-- =========================================================================================
    File Name: Explore.vue
    Description: Explore panel to select guided tags
    ----------------------------------------------------------------------------------------
    Item Name: NexSat
    Author: Anand
========================================================================================== -->

<template>
  <div id="explore-box">
    <vx-card
      class="mb-base"
      :title="guide.subtitle"
      :subtitle="guide.note + (guide.mode ? ' $ Sales' : ' quantity sold')"
      :card-background="guide.type ? '' : ''"
      v-show="showExplore"
    >
      <template slot="actions">
        <div class="flex flex-wrap">
          <div class="btn-group">
            <vs-button
              radius
              type="line"
              v-for="tl in timeline"
              :key="tl.days"
              v-bind="{disabled : tl.block}"
              @click="timelineLookup(tl)"
              size="small" :color="tl.selected ? colors.primary : '#aaa'"
              >
              {{tl.label}}
            </vs-button>
          </div>
          <div class="btn-group ml-4 mt-3">
            <feather-icon
              icon="GridIcon"
              svgClasses="w-6 h-6"
              :class="guide.type ? 'text-primary' : 'text-grey'"
              @click="showGuide"
            ></feather-icon>
          </div>
        </div>
      </template>

      <div class="vx-row mt-4 md:flex-row flex-col">
        <!-- SEARCH BOX -->
        <div class="vx-col md:w-3/5 lg:w-2/3 w-full">
          <multiselect
            v-model="multiselect.exploreTags"
            id="ajax"
            label="tag"
            track-by="tag"
            placeholder="Type here to explore"
            open-direction="bottom"
            :allow-empty="false"
            :options="multiselect.tags"
            :multiple="true"
            :searchable="true"
            :loading="multiselect.isLoading"
            :internal-search="true"
            :clear-on-select="true"
            :close-on-select="false"
            :max="5"
            :options-limit="50"
            :limit="5"
            :limit-text="limitText"
            :max-height="600"
            :show-no-results="true"
            :hide-selected="true"
            @search-change="queryTags"
          >
            <template slot="tag" slot-scope="{ option, remove }">
              <vs-chip
                transparent
                @click="remove(option), explore()"
                :color="option.type !== 'S' ? '' : 'primary'"
                type="gradient"
                v-bind="{closable : option.type !== 'S'}"
              >
                <vs-avatar
                  :text="option.type !== 'S' ? option.type : ''"
                  icon="icon-map-pin"
                  icon-pack="feather"
                />
                {{ option.tag }}
              </vs-chip>
            </template>
            <template slot="option" slot-scope="props">
              <div class="flex justify-between">
                <span class="option__title">{{ props.option.tag | ellipsis(50) }}</span>
                <vs-chip>{{ multiselect.tagTypes[props.option.type] }}</vs-chip>
              </div>
            </template>
            <template slot="clear" slot-scope="props">
              <div
                class="multiselect__clear"
                v-if="multiselect.exploreTags.length"
                @mousedown.prevent.stop="clearAll(props.search)"
              ></div>
            </template>
            <span slot="noResult">Oops! No elements found. Consider changing the search query.</span>
          </multiselect>
        </div>
        <div class="vx-col md:w-2/5 lg:w-1/3 w-full">
          <div class="vx-row">
            <!-- DATE BOX -->
            <div class="vx-col sm:my-0 my-3 flex justify-center">
              <div class="flex items-center">
                <date-pick range :dateScope="allowedDateScope" @dateRange="datePickUpdate"></date-pick>
              </div>
            </div>
            <!-- EXPLORE BUTTON -->
            <div class="vx-col ml-1 sm:my-0 my-3 flex justify-center">
              <vs-button
                type="filled"
                color="primary"
                gradient-color-secondary="rgb(130, 207, 23)"
                icon-pack="feather"
                icon="icon-search"
                @click="explore()"
                v-bind="{disabled : isLoading}"
                ref="exploreButton"
                id="exploreButton"
                class="vs-con-loading__container"
                vslor="primary"
              >{{exploreLabel}}</vs-button>
            </div>
          </div>
        </div>
      </div>
      <!-- <div class="vx-row mt-10 md:flex-row flex-col" v-if="isLoading">
        <vs-progress indeterminate color="primary"></vs-progress>
      </div>-->
    </vx-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import Multiselect from "vue-multiselect";
import DatePick from "@/app/shared/components/DatePick.vue";
//you need to import the CSS manually (in case you want to override it)
import "vue-multiselect/dist/vue-multiselect.min.css";

import { appCommons } from "@/app/shared/services";
import appConfig from "@/../appConfig.js";
import { fetchTags } from "../shared/services";

import { actionsTypes } from "../shared/state";

export default {
  props: {},
  data() {
    return {
      showExplore: false,
      exploreLabel: "Explore",

      backgroundLoading: "primary",
      colorLoading: "#fff",

      colors: appConfig.colors,
      timeline: [{
        label: "1 w",
        desc: "1 week",
        days: 7,
        selected: true
      }, {
        label: "2 w",
        desc: "2 weeks",
        days: 14,
        selected: false
      }, {
        label: "4 w",
        desc: "4 weeks",
        days: 28,
        selected: false
      }, {
        label: "5 w",
        desc: "5 weeks",
        days: 35,
        selected: false
      }, {
        label: "13 w",
        desc: "13 weeks",
        days: 91,
        selected: false
      }, {
        label: "26 w",
        desc: "26 weeks",
        days: 182,
        selected: false
      }, {
        label: "52 w",
        desc: "52 weeks",
        days: 364,
        selected: false
      }],
      activeTimeline: {
        label: "1 w",
        desc: "1 week",
        days: 7,
        selected: true
      },


      payload: null,

      //MultiSelect Config
      multiselect: {
        exploreTags: [],
        tags: [],
        isLoading: false,
        selected: null,
        tagTypes: {
          S: "Store",
          M: "Manufacturer",
          B: "Brand",
          C: "Category",
          U: "Sku",
          G: "Sub Category",
          D: "Department"
        }
        // options: ['list', 'of', 'options'],
      },

      //Calendar Config
      calendar: {
        opens: "left",
        minDate: "07/28/2019",
        maxDate: "08/03/2019",
        dateRange: {
          startDate: "07/28/2019",
          endDate: "08/03/2019",
          span: []
        },
        ranges: false,
        show_ranges: true,
        singleDatePicker: false,
        timePicker: false,
        timePicker24Hour: false,
        showDropdowns: true,
        autoApply: true,
        showWeekNumbers: true,
        linkedCalendars: true
      },

      allowedDateScope: {}
    };
  },
  methods: {
    // Multiselect specific methods
    limitText(count) {
      return `and ${count} other tags`;
    },
    queryTags(query) {
      this.multiselect.isLoading = true;
      fetchTags()
        .then(data => {
          this.tempTags = data.data;
          this.multiselect.tags = this.filterAndSortTags();
          this.multiselect.isLoading = false;
        })
        .catch(error => {
          this.multiselect.isLoading = false;
          throw new Error(error);
        });
    },
    clearAll() {
      this.multiselect.exploreTags = [];
    },

    // Calendar Specific methods
    blockDates(date) {
      let minDate = this.calendar.minDate
        ? new Date(this.calendar.minDate)
        : new Date("01/01/2017");
      let maxDate = this.calendar.maxDate
        ? new Date(this.calendar.maxDate)
        : new Date();

      return date < minDate || date > maxDate;
    },
    datePickUpdate(val) {
      this.calendar.dateRange = val;
    },

    // GO : Explore specific methods
    showGuide() {
      // this.showExplore = false;
      this.guide = {
        title: "",
        subtitle: "Explore performance of Brands, categories and channels",
        note: "Performance is calculated using number of ",
        ref: "",
        mode: true,
        type: "",
        minCount: 0
      };
      this.$store.commit("sandbox/[SANDBOX] SET_SANDBOX_GUIDE_ON", this.guide);
    },
    init() {
      if (this.guide && this.guide.type) {
        this.dateScope();
        this.multiselect.exploreTags = appCommons.currentScopeTags();
        this.queryTags();
        this.explore();
        this.showExplore = true;
      } else {
        this.dateScope();
        this.multiselect.exploreTags = [];
        this.queryTags();
        this.explore();
        // this.showExplore = false;
      }
    },

    timelineLookup(tl) {
      for(var t of this.timeline) {
        if(t.days === tl.days) {
          t.selected = true;
          this.activeTimeline = t;
        } else {
          t.selected = false;
        }
      }
      this.dateScope();
      this.explore();
    },
    explore() {
      let exploreTagTypes = [];
      let queryType = "";
      let triggerMode = "revenue";
      let dateRange = {};
      let query = "";
      let mock = this.guide && this.guide.mock;

      dateRange = Object.assign(dateRange, this.calendar.dateRange);

      this.multiselect.exploreTags = this.bucketAndSortTags(
        this.multiselect.exploreTags
      );

      if (this.multiselect.exploreTags.length > 1) {
        if (this.guide && this.guide.type) {
          queryType = this.guide.type;
          triggerMode = this.guide.mode ? "revenue" : "quantity";
        }

        //Dummy query for dev purpose and later will use recent seaerches from cache to fill this
        query =
          queryType +
          " - " +
          this.multiselect.exploreTags[0].tag +
          " vs " +
          this.flatTags(this.multiselect.exploreTags);
      }

      this.payload = {
        tags: this.multiselect.exploreTags,
        dateRange: dateRange,
        query: query,
        mode: triggerMode,
        type: queryType,
        res: {},
        mock: mock
      };

      this.$store.dispatch(
        "sandbox/" + actionsTypes.FETCH_SANDBOX_ARTEFACT,
        this.payload
      );

      if (
        this.guide &&
        this.multiselect.exploreTags.length < this.guide.minCount
      ) {
        this.$vs.notify({
          time: 3000,
          title: "Info",
          text: "Provide minimum input criteria",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning"
        });
      }

      this.filterReset = true;

      return true;
    },
    bucketAndSortTags(etags) {
      let tagBucket = [];
      let i = 0;
      let len = etags.length;
      // S - STORE, M - MANUFACTURER, B - BRAND, C - CATEGORY, U - SKU, G - SUBCAT ( GROUP ), D - DEPT
      let tagBuckets = {
        B: [],
        C: [],
        U: [],
        S: [],
        M: [],
        G: [],
        D: [],
        order: "SMBCUGD"
      };

      for (; i < len; i++) {
        tagBuckets[etags[i].type].push(etags[i]);
      }

      for (let j = 0; j < tagBuckets.order.length; j++) {
        Array.prototype.push.apply(
          tagBucket,
          tagBuckets[tagBuckets.order.charAt(j)]
        );
      }

      return tagBucket;
    },
    flatTags(etags) {
      let i = 1;
      let len = etags.length;
      let flatTags = "";

      for (; i < len; i++) {
        flatTags = flatTags.concat(etags[i].tag + ", ");
      }

      return flatTags.slice(0, -2);
    },
    filterAndSortTags() {
      let filteredTags = this.tempTags.filter(
        t =>
          this.guide.allowedTags &&
          this.guide.allowedTags.indexOf(t.type) > -1 &&
          appCommons.scopeFilter(t)
      );

      return filteredTags.sort(function(t1, t2) {
        var tA = t1.tag.toUpperCase();
        var tB = t2.tag.toUpperCase();
        if (tA < tB) {
          return -1;
        }
        if (tA > tB) {
          return 1;
        }

        // tags must be equal
        return 0;
      });
    },
    dateScope() {
      let dateScope = appCommons.allowedDateScope(
        "",
        // this.guide.timeWindow,
        this.activeTimeline.days,
        "minus",
        this.guide.minDate,
        this.guide.maxDate
      );

      this.allowedDateScope = dateScope;

      this.calendar.minDate = dateScope.minDate;
      this.calendar.maxDate = dateScope.maxDate;
      this.calendar.dateRange.startDate = dateScope.startDate;
      this.calendar.dateRange.endDate = dateScope.endDate;
    },
    exploreStatus() {
      return this.isLoading ? "Loading ..." : "Explore";
    },

    ...mapGetters({ guide: "sandbox/guideOn", isLoading: "sandbox/isLoading" })
  },
  computed: {},
  mounted() {
    this.$store.watch(
      (state, getters) => getters["sandbox/guideOn"],
      (newValue, oldValue) => {
        this.guide = newValue;
        this.init();
      }
    );

    this.$store.watch(
      (state, getters) => getters["sandbox/isLoading"],
      (newValue, oldValue) => {
        this.isLoading = newValue;
        if (this.isLoading) {
          this.exploreLabel = "In Progress ...";
          this.$vs.loading({
            // background: this.backgroundLoading,
            // color: this.colorLoading,
            // container: "#exploreButton",
            // scale: 0.45,
            type: "radius"
          });
        }

        if (!this.isLoading) {
          this.exploreLabel = "Explore";
          // this.$vs.loading.close("#exploreButton > .con-vs-loading");
          this.$vs.loading.close();
        }
      }
    );
  },
  watch: {
    "$store.state.AppScopeTrack"(val) {
      this.init();
    }
  },
  components: {
    Multiselect,
    DatePick
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/pages/search.scss";

#explore-box {
  .reportrange-text {
    background: transparent;
    cursor: pointer;
    padding: 0px;
    border: 0px;
  }
  .multiselect__option--highlight {
    // background: aqua;
  }
}
</style>
